"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Z } from "@/lib/z";

/**
 * Глобальный делегированный тултип портала. Монтируется ОДИН раз (в layout).
 * Один слушатель на документе читает атрибут `data-tooltip` у наведённого
 * элемента и рисует ОДНУ портальную всплывашку — вместо тёмного нативного
 * `title=` и без тысяч React-Popover'ов в больших таблицах (нулевой оверхед).
 *
 *   <button data-tooltip="Скачать">…            // сторона по умолчанию — top
 *   <div data-tooltip="…" data-tooltip-side="right">
 *
 * Стиль совпадает с компонентом <Tooltip> (components/ui/tooltip.tsx), который
 * используется в точечных интерактивных местах (отдел HR).
 */

type Shown = { text: string; rect: DOMRect; side: string };
const SHOW_DELAY = 180;

export function GlobalTooltip() {
  const [shown, setShown] = useState<Shown | null>(null);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const curRef = useRef<Element | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const hide = () => {
      clearTimer();
      curRef.current = null;
      setShown(null);
      setCoords(null);
    };
    const scheduleShow = (el: Element) => {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (curRef.current !== el || !el.isConnected) return;
        const text = el.getAttribute("data-tooltip");
        if (!text) return;
        setCoords(null);
        setShown({ text, rect: el.getBoundingClientRect(), side: el.getAttribute("data-tooltip-side") || "top" });
      }, SHOW_DELAY);
    };
    const enter = (t: EventTarget | null) => {
      const el = t instanceof Element ? t.closest("[data-tooltip]") : null;
      if (!el || !el.getAttribute("data-tooltip")) return;
      if (el === curRef.current) return;
      curRef.current = el;
      scheduleShow(el);
    };
    const onOver = (e: PointerEvent) => enter(e.target);
    const onOut = (e: PointerEvent) => {
      const cur = curRef.current;
      if (!cur) return;
      const to = e.relatedTarget as Node | null;
      if (to && cur.contains(to)) return;
      hide();
    };
    const onFocusIn = (e: FocusEvent) => enter(e.target);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("pointerout", onOut, true);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("wheel", hide, { capture: true, passive: true });
    return () => {
      clearTimer();
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("wheel", hide, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!shown || !tipRef.current) return;
    const gap = 6;
    const margin = 6;
    const tip = tipRef.current.getBoundingClientRect();
    const { rect } = shown;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let s = shown.side;
    let left: number;
    let top: number;
    if (s === "left" || s === "right") {
      if (s === "left" && rect.left - tip.width - gap < margin) s = "right";
      else if (s === "right" && rect.right + tip.width + gap > vw - margin) s = "left";
      top = rect.top + rect.height / 2 - tip.height / 2;
      left = s === "left" ? rect.left - tip.width - gap : rect.right + gap;
    } else {
      if (s === "top" && rect.top - tip.height - gap < margin) s = "bottom";
      else if (s === "bottom" && rect.bottom + tip.height + gap > vh - margin) s = "top";
      left = rect.left + rect.width / 2 - tip.width / 2;
      top = s === "top" ? rect.top - tip.height - gap : rect.bottom + gap;
    }
    left = Math.min(Math.max(margin, left), Math.max(margin, vw - tip.width - margin));
    top = Math.min(Math.max(margin, top), Math.max(margin, vh - tip.height - margin));
    setCoords({ left, top });
  }, [shown]);

  if (!shown) return null;
  return (
    <div
      ref={tipRef}
      role="tooltip"
      className="pointer-events-none fixed w-max max-w-[280px] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[11px] font-normal normal-case leading-snug tracking-normal text-[var(--text)] shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
      style={{
        zIndex: Z.TOOLTIP,
        left: coords?.left ?? -9999,
        top: coords?.top ?? -9999,
        visibility: coords ? "visible" : "hidden",
        whiteSpace: "pre-line",
      }}
    >
      {shown.text}
    </div>
  );
}
